import { useEffect, useReducer, useRef, useState } from "react"
import { useRecorderPermission } from "../../hooks/use-recorder-permission"
import {
    APP_INTERACTION_TYPES,
    AUDIO_ASSESSMENT_STAGE,
    RECORDING_PROGRESS_TIME,
} from "../../shared/constants"
import { AUDIO_ASSESSMENT_EVENTS, EVENTS } from "../../shared/constants/MIXAPANEL_EVENTS"
import { useAssessmentStore } from "../../shared/store"
import { uploadAudioAssessmentRecording } from "../../shared/utils/api-client"
import { CompletionObjectType } from "../../shared/utils/assessment-types"
import { trackMixpanelEvent } from "../../utils/helpers"
import {
    audioAssessmentReducer,
    initialState,
} from "./audio-assessment-reducer/audio-assessment-reducer"
import CountdownScreen from "./countdown-screen"
import PreRecordingScreen from "./pre-recording-screen"
import RecordingScreen from "./recording-screen"
import RecordingSuccessfulScreen from "./recording-successful-screen"

interface AssessmentProps {
    handleOnCompletion: (completionObject: CompletionObjectType) => void
    handleSkip: () => void
}
const AudioAssessment = ({ handleOnCompletion, handleSkip }: AssessmentProps) => {
    const { currentStep, assesmentQuestions } = useAssessmentStore((state) => state)
    const isVasFlow = assesmentQuestions?.mandatory
    const audioUploadURL =
        assesmentQuestions?.questions?.[currentStep]?.questions[0]?.upload_data
            ?.upload_urls?.[0]
    const signedHeaders =
        assesmentQuestions?.questions?.[currentStep]?.questions[0]?.upload_data
            ?.signed_headers
    const initializeRecoder = useRecorderPermission("audio") as any
    const [audioSrc, setAudioSrc] = useState("")
    const [state, dispatch] = useReducer(audioAssessmentReducer, initialState)
    const [blobData, setBlobData] = useState()
    const recorderRef = useRef() as any
    const questionId = assesmentQuestions?.questions[currentStep]?.questions[0]?.id
    const questionType = assesmentQuestions?.questions[currentStep]?.question_type
    const sampleAudioSrc =
        "https://cdn.apna.co/cloudinary/android_assets/english_audio_intro_sample.m4a"
    const handleStartRecording = async () => {
        const recorder = await initializeRecoder()
        recorderRef.current = recorder
        dispatch({ type: "update-current-state", payload: "countdown" })
        setTimeout(() => {
            dispatch({ type: "update-current-state", payload: "recording" })
            recorderRef.current?.startRecording()
            trackMixpanelEvent(
                AUDIO_ASSESSMENT_EVENTS.ENGLISH_AUDIO_INTRO_RECORDING_STARTED,
                {
                    pageType: isVasFlow ? "VAS" : "Application Flow Enrichment",
                }
            )
        }, 3000)
    }
    const handleStopRecording = async () => {
        dispatch({ type: "update-is-recording", payload: false })
        trackMixpanelEvent(
            AUDIO_ASSESSMENT_EVENTS.ENGLISH_AUDIO_INTRO_RECORDING_STOPPED,
            {
                pageType: isVasFlow ? "VAS" : "Application Flow Enrichment",
            }
        )
        if (state.recordingTimeElapsed < 30) {
            recorderRef.current?.stopRecording()
            dispatch({ type: "set-recording-error", payload: true })
            dispatch({ type: "update-current-state", payload: "pre-recording" })
        }
        if (state.recordingTimeElapsed >= 30) {
            dispatch({ type: "set-recording-successful", payload: true })
            await recorderRef.current?.stopRecording()
            const blob = await recorderRef.current.getBlob()
            setAudioSrc(window.URL.createObjectURL(blob))
            setBlobData(blob)
            setTimeout(() => {
                dispatch({ type: "update-current-state", payload: "recording-success" })
            }, 3000)
        }
        dispatch({ type: "update-countdown-value", payload: 3 })
    }
    const handleRecordAgainClick = () => {
        recorderRef.current?.reset()
        dispatch({ type: "set-recording-error", payload: false })
        dispatch({ type: "set-recording-successful", payload: false })
        trackMixpanelEvent(
            AUDIO_ASSESSMENT_EVENTS.ENGLISH_AUDIO_INTRO_RECORD_AGAIN_CLICKED,
            {
                pageType: isVasFlow ? "VAS" : "Application Flow Enrichment",
            }
        )
        handleStartRecording()
    }
    const handleUploadClick = async () => {
        const completionObject = {
            question_id: questionId,
            question_type: questionType,
        }
        dispatch({ type: "set-upload-state", payload: "uploading" })
        trackMixpanelEvent(AUDIO_ASSESSMENT_EVENTS.ENGLISH_AUDIO_INTRO_UPLOAD_CLICKED, {
            pageType: isVasFlow ? "VAS" : "Application Flow Enrichment",
        })
        if (blobData) {
            const url = audioUploadURL
            try {
                await uploadAudioAssessmentRecording(url, blobData, {
                    ...signedHeaders,
                    "Content-Type": "application/octet-stream",
                })
                dispatch({ type: "set-upload-state", payload: "upload-success" })
                handleOnCompletion(completionObject)
                trackMixpanelEvent(
                    AUDIO_ASSESSMENT_EVENTS.ENGLISH_AUDIO_INTRO_UPLOAD_RESULT,
                    {
                        Result: "Success",
                    }
                )
                if (isVasFlow) {
                    trackMixpanelEvent(EVENTS.ASSESSMENT_PARTIAL_EVALUATION, {
                        "Section Name": "VAS English",
                        "Section Result": "Pending",
                    })
                }
            } catch (e) {
                dispatch({ type: "set-upload-state", payload: "upload-failure" })
                trackMixpanelEvent(
                    AUDIO_ASSESSMENT_EVENTS.ENGLISH_AUDIO_INTRO_UPLOAD_RESULT,
                    {
                        Result: "Failure",
                    }
                )
            }
        }
    }
    // Control countdown
    useEffect(() => {
        if (
            state.currentState === AUDIO_ASSESSMENT_STAGE.COUNTDOWN &&
            state.preRecordingCountDownValue > 0
        ) {
            setTimeout(() => {
                dispatch({
                    type: "update-countdown-value",
                    payload: state.preRecordingCountDownValue - 1,
                })
            }, 1000)
        }
    }, [state.currentState, state.preRecordingCountDownValue])

    // Control recording time progress
    useEffect(() => {
        if (
            state.currentState === AUDIO_ASSESSMENT_STAGE.RECORDING &&
            state.isRecording &&
            state.recordingTimeElapsed < 60
        ) {
            setTimeout(() => {
                // setTimeElapsed((prev) => prev + 1)
                dispatch({
                    type: "update-recording-time-elapsed",
                    payload: state.recordingTimeElapsed + 1,
                })
            }, 1000)
        }
        if (
            state.currentState === AUDIO_ASSESSMENT_STAGE.RECORDING &&
            state.recordingTimeElapsed === RECORDING_PROGRESS_TIME.maximum
        ) {
            // Stop Recording
            handleStopRecording()
        }
        // TODO: raghav to check this dependencies why there is a warning
    }, [state.currentState, state.recordingTimeElapsed])

    useEffect(() => {
        trackMixpanelEvent(EVENTS.ASSESSMENT_PAGE_SHOWN, {
            "Section Name": "VAS English",
            "is VAS Present": true,
        })
        const handleOnBackPress = () => {
            console.log("Back button pressed")
        }
        window.addEventListener(APP_INTERACTION_TYPES.BACK_PRESS, handleOnBackPress)

        return () => {
            window.removeEventListener(
                APP_INTERACTION_TYPES.BACK_PRESS,
                handleOnBackPress
            )
        }
    }, [])

    useEffect(() => {
        if (assesmentQuestions?.questions?.length > 0) {
            const state = useAssessmentStore.getState()
            const isVasFlow = state.assesmentQuestions.mandatory
            const currentStep = state.currentStep
            const isAudioQuestion =
                assesmentQuestions.questions[currentStep]?.question_type === "AudioUpload"

            if (isAudioQuestion) {
                trackMixpanelEvent(EVENTS.ENRICHMENT_SCREEN_SHOWN, {
                    Type: isVasFlow ? "VAS" : "Application flow enrichment",
                    "Entity Shown": "Audio assesment",
                })
            }
        }
    }, [assesmentQuestions])

    return (
        <div className="flex flex-col w-full">
            {/* Audio Assessment Container */}
            <div className="flex flex-col items-center justify-between h-[100%]">
                {/* STAGE 1: Sample Audio Container and Instructions */}
                {state.currentState === AUDIO_ASSESSMENT_STAGE.PRE_RECORDING && (
                    <PreRecordingScreen
                        audioSrc={sampleAudioSrc}
                        handleStartRecording={handleStartRecording}
                        isRecordingError={state.recordingError}
                        handleSkip={handleSkip}
                    />
                )}
                {/* STAGE 2: Countdown Container */}
                {state.currentState === AUDIO_ASSESSMENT_STAGE.COUNTDOWN && (
                    <CountdownScreen countdownNumber={state.preRecordingCountDownValue} />
                )}
                {/* STAGE 3: Recording Screen */}
                {state.currentState === AUDIO_ASSESSMENT_STAGE.RECORDING && (
                    <RecordingScreen
                        timeElapsed={state.recordingTimeElapsed}
                        isRecordingSuccess={state.isRecordingSuccessful}
                        handleStopRecording={handleStopRecording}
                    />
                )}
                {/* STAGE 4: Recording Success Screen */}
                {state.currentState === AUDIO_ASSESSMENT_STAGE.RECORDING_SUCCESS && (
                    <RecordingSuccessfulScreen
                        audioSrc={audioSrc}
                        handleRecordAgainClick={handleRecordAgainClick}
                        handleUploadClick={handleUploadClick}
                        uploadStatus={state.uploadState}
                    />
                )}
            </div>
        </div>
    )
}
export default AudioAssessment
